import { toSvg } from 'html-to-image';
import {
  createContext,
  PropsWithChildren,
  RefObject,
  useContext,
  useState,
  useEffect,
  Dispatch,
  useMemo,
  useCallback,
} from 'react';
// eslint-disable-next-line no-restricted-imports
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from 'context';
import { useMixpanelTracking } from 'hooks';

type SnapshotMap = Record<string, string>;
export type NavAnimationDirection = 'forward' | 'backward' | 'none';
export type NavigationPath = string | -1 | 0;

const SnapshotContext = createContext<{
  setSnapshots: Dispatch<React.SetStateAction<SnapshotMap>>;
  snapshots: SnapshotMap | undefined;
  setNavDirection: Dispatch<React.SetStateAction<NavAnimationDirection>>;
  navDirection: NavAnimationDirection;
  previousLocation: string | null;
  setPreviousLocation: Dispatch<React.SetStateAction<string | null>>;
}>({
  snapshots: undefined,
  setSnapshots: () => {},
  setNavDirection: () => {},
  navDirection: 'none',
  previousLocation: null,
  setPreviousLocation: () => {},
});

export const SnapshotNavigationProvider = ({ children }: PropsWithChildren) => {
  const [snapshots, setSnapshots] = useState<SnapshotMap>({});
  const [navDirection, setNavDirection] =
    useState<NavAnimationDirection>('forward');
  const [previousLocation, setPreviousLocation] = useState<string | null>(null);

  const contextValue = useMemo(
    () => ({
      snapshots,
      setSnapshots,
      navDirection,
      setNavDirection,
      previousLocation,
      setPreviousLocation,
    }),
    [snapshots, navDirection, previousLocation]
  );

  return (
    <SnapshotContext.Provider value={contextValue}>
      {children}
    </SnapshotContext.Provider>
  );
};

export const useSetElementToSnapshot = (element: RefObject<HTMLDivElement>) => {
  const { setSnapshots } = useContext(SnapshotContext);
  const location = useLocation();

  useEffect(() => {
    const takeScreenshot = () => {
      if (!element.current) return;
      toSvg(element.current, {
        backgroundColor: 'transparent',
        includeQueryParams: true,
      })
        .then((svg) => {
          setSnapshots((prev) => ({
            ...prev,
            [location.pathname]: svg,
          }));
        })
        .catch(console.error);
    };
    requestAnimationFrame(() => {
      // A delay of 450ms is used because the forward animation takes 400ms
      setTimeout(takeScreenshot, 450);
    });
  }, [element, location.pathname, setSnapshots]);
};

export const useNavigateWithSnapshots = () => {
  const navigate = useNavigate();
  const mixpanelTrackEvent = useMixpanelTracking();
  const location = useLocation();
  const { setNavDirection, setPreviousLocation } = useContext(SnapshotContext);
  const session = useSession();

  const navigateWithSnapshots = useCallback(
    (path: NavigationPath, direction: NavAnimationDirection) => {
      if (session?.userId) {
        mixpanelTrackEvent(session.userId, 'User navigated', {
          from: location.pathname,
          to: path,
          direction,
          userId: session.userId,
        });
      }
      const performNavigation = (navPath: NavigationPath) => {
        if (navPath === 0 || navPath === -1) {
          navigate(navPath);
        } else {
          navigate(navPath);
        }
      };

      switch (direction) {
        case 'none':
          performNavigation(path);
          setPreviousLocation(null);
          setNavDirection('none');
          break;
        case 'forward':
          performNavigation(path);
          setPreviousLocation(location.pathname);
          setNavDirection('forward');
          break;
        case 'backward':
          performNavigation(path);
          setPreviousLocation(null);
          setNavDirection('backward');
          break;
      }
    },
    [
      navigate,
      location.pathname,
      setNavDirection,
      setPreviousLocation,
      mixpanelTrackEvent,
      session?.userId,
    ]
  );

  return navigateWithSnapshots;
};

export const useSnapshots = () => {
  const { snapshots, navDirection, previousLocation } =
    useContext(SnapshotContext);
  return { snapshots, navDirection, previousLocation };
};
